// What one game card shows, decided without React: which parts of a game are
// visible for the current score mode and hidden teams, how clocks and goal
// times are written and which names are highlighted.
// Pure functions, so every rule is unit-testable (gameViewModel.test.ts).

import GameStatus, { type DerivedStatus } from '../../core/model/gameStatus';
import { abbrevOfTeam, type TeamAbbrev } from '../../core/model/teams';
import type { Game, GameId, Goal, LocalizedName, Player, PlayerId } from '../../core/model/types';
import type { ScoreFilter } from '../../core/settings/settings';
import type { TranslationKey } from '../../i18n/en';

export type Translate = (key: TranslationKey) => string;

export interface CardContext {
    // The selected country's players.
    players: readonly Player[];
    scoreFilter: ScoreFilter;
    // Abbreviations of the hidden teams, when hiding is switched on.
    hiddenTeams: readonly string[];
    // The league's team abbreviations by id (default: the NHL's).
    teamAbbrev?: TeamAbbrev;
    // The card's own "show" button has been pressed.
    revealed: boolean;
    t: Translate;
    language: 'fi' | 'en';
    now: Date;
}

export interface NameModel {
    text: string;
    tracked: boolean;
}

export interface GoalRowModel {
    key: number;
    time: string;
    score: string;
    scorer: NameModel;
    detail: string;
    assists: NameModel[];
    videoUrl?: string;
}

export interface GoalieRowModel {
    key: number;
    team: string;
    name: string;
    tracked: boolean;
    saves: string;
    savePct: string;
}

export interface RosterEntry {
    name: string;
    injury: number;
    dimmed: boolean;
}

export interface RosterRowModel {
    key: number;
    home?: RosterEntry;
    away?: RosterEntry;
}

// 'full': the normal card. 'hidden-team': one line, the game involves a team
// the user hides. 'locked': one line, scores are switched off.
export type CardMode = 'full' | 'hidden-team' | 'locked';

export interface GameCardModel {
    key: GameId;
    status: DerivedStatus;
    mode: CardMode;
    home: string;
    away: string;
    score?: string;
    // Elapsed game time of a live game.
    clock?: string;
    // "OT" / "SO" label of a game that ended after regulation.
    ending?: string;
    // Start time, "starts soon", postponed or to be decided.
    startLabel?: string;
    recapUrl?: string;
    longUrl?: string;
    meta?: string;
    goals: GoalRowModel[];
    goalies: GoalieRowModel[];
    roster?: RosterRowModel[];
    // The part of the card that collapsing hides.
    hasBody: boolean;
}

const SHOT_TYPE_KEYS: Record<string, TranslationKey> = {
    'tip-in': 'shotType_tipIn',
    'wrist': 'shotType_wristShot',
    'slap': 'shotType_slapShot',
    'deflected': 'shotType_deflected',
    'snap': 'shotType_snapShot',
    'backhand': 'shotType_backhand',
    'wrap-around': 'shotType_wrapAround',
    'poke': 'shotType_poke',
    'bat': 'shotType_bat',
    'between-legs': 'shotType_betweenLegs',
};

// Pregame roster players whose latest game is further back than this are
// dimmed, as they are likely out of the lineup.
export const INACTIVE_AFTER_DAYS = 35;
const MS_PER_DAY = 24 * 3600 * 1000;

const pad2 = (value: number) => String(value).padStart(2, '0');
const compare = (a: string | number, b: string | number) => (a > b ? 1 : (a < b ? -1 : 0));

export const sortGames = (games: readonly Game[]): Game[] =>
    [...games].sort((a, b) => compare(a.startTime, b.startTime) || compare(String(a.gameId), String(b.gameId)));

const trackedIds = (players: readonly Player[]): Set<PlayerId> => new Set(players.map(player => player.playerId));

const parseClock = (clock: string): number => {
    const [minutes, seconds] = clock.split(':').map(Number);
    return minutes * 60 + seconds;
};

// Elapsed game time of a live game ("27:26"): regulation periods and playoff
// overtimes are 20 minutes, regular season overtime ends at 65:00.
export function formatGameClock(game: Game): string {
    const remaining = game.cpTimeRemaining === 'END' ? 0 : parseClock(game.cpTimeRemaining);
    const periodEndMinutes = (game.currentPeriod <= 3 || game.type === GameStatus.GAME_TYPE.PLAYOFF)
        ? game.currentPeriod * 20
        : 65;
    const elapsed = periodEndMinutes * 60 - remaining;
    return `${pad2(Math.floor(elapsed / 60))}:${pad2(elapsed % 60)}`;
}

// Goal time counted from the start of the game; shootout goals sit at 65:00.
export function formatGoalTime(goal: Goal): string {
    if (goal.ordinalNum === 'SO') {
        return '65:00';
    }
    const [minutes, seconds] = goal.time.split(':');
    return `${pad2((goal.period - 1) * 20 + parseInt(minutes, 10))}:${seconds}`;
}

export const formatGoalScore = (goal: Goal, t: Translate): string =>
    goal.ordinalNum === 'SO' ? t('gameEndingType_so') : `${goal.homeGoals}–${goal.awayGoals}`;

// "(Wrist shot, PP, EN)"; unknown shot types are shown as received.
export function formatGoalDetail(goal: Goal, t: Translate): string {
    const known = goal.type !== undefined ? SHOT_TYPE_KEYS[goal.type] : undefined;
    const shotType = known !== undefined ? t(known) : goal.type;
    if (shotType === undefined) {
        return '';
    }
    const parts = [shotType];
    if (goal.strength === 'pp') {
        parts.push(t('goalType_pp'));
    } else if (goal.strength === 'sh') {
        parts.push(t('goalType_sh'));
    }
    if (goal.isEmptyNet) {
        parts.push(t('goalType_en'));
    }
    return `(${parts.join(', ')})`;
}

export const localizedName = (name: LocalizedName | undefined, language: 'fi' | 'en'): string | undefined =>
    (language === 'fi' && name?.fi !== undefined) ? name.fi : name?.default;

export function formatSeriesStatus(game: Game, t: Translate): string {
    if (game.seriesStatus === undefined || game.seriesStatus === '') {
        return t('scores_notStarted');
    }
    return game.seriesStatus
        .replace('lead', t('scores_lead'))
        .replace('series tied', t('scores_tied'))
        .replace('win', t('scores_win'))
        .replace('series not started', t('scores_notStarted'));
}

// The start time (or "soon" once a pregame is past its start time), PPD or TBD
// of a game that has not started, otherwise undefined.
export function formatStartLabel(game: Game, status: DerivedStatus, t: Translate, now: Date): string | undefined {
    if (status.isScheduled || status.isPreGame) {
        const startTime = new Date(game.startTime);
        if (status.isPreGame && startTime < now) {
            return t('scores_startsSoon');
        }
        return `${pad2(startTime.getHours())}:${pad2(startTime.getMinutes())}`;
    }
    if (status.isPostponed) {
        return t('gameStatus_ppd');
    }
    if (status.isTBD) {
        return t('gameStatus_tbd');
    }
    return undefined;
}

// The "OT" / "SO" label of a game that ended after regulation.
const formatEnding = (status: DerivedStatus, t: Translate): string | undefined => {
    if (status.isOvertime) {
        return t('gameEndingType_ot');
    }
    if (status.isShootout) {
        return t('gameEndingType_so');
    }
    return undefined;
};

// Preseason and playoff games get a line of their own; the series standing
// gives results away, so it is left out while scores are switched off.
export function formatMetaLine(game: Game, status: DerivedStatus, scoreFilter: ScoreFilter, t: Translate): string | undefined {
    if (game.type === GameStatus.GAME_TYPE.PRESEASON) {
        return t('scores_preSeasonGame');
    }
    if (game.type === GameStatus.GAME_TYPE.PLAYOFF) {
        const gameNumber = `${t('scores_game')} ${game.gameNumber}`;
        return (scoreFilter !== 'none' || status.isNotYetStarted)
            ? `${gameNumber}, ${formatSeriesStatus(game, t)}`
            : gameNumber;
    }
    return undefined;
}

const nameModel = (name: LocalizedName | undefined, id: PlayerId | undefined, tracked: Set<PlayerId>, language: 'fi' | 'en'): NameModel | undefined => {
    const text = localizedName(name, language);
    return text === undefined ? undefined : { text, tracked: id !== undefined && tracked.has(id) };
};

const isRowShown = (scoreFilter: ScoreFilter, highlighted: boolean) =>
    scoreFilter === 'all' || (scoreFilter === 'filtered' && highlighted);

function buildGoalRows(game: Game, tracked: Set<PlayerId>, scoreFilter: ScoreFilter, t: Translate, language: 'fi' | 'en'): GoalRowModel[] {
    const rows: GoalRowModel[] = [];
    game.goals.forEach((goal, index) => {
        const scorer = nameModel(goal.scorerName, goal.scorerId, tracked, language) ?? { text: '', tracked: false };
        const assists = [
            nameModel(goal.assistOneName, goal.assistOneId, tracked, language),
            nameModel(goal.assistTwoName, goal.assistTwoId, tracked, language),
        ].filter((name): name is NameModel => name !== undefined);
        const highlighted = scorer.tracked || assists.some(assist => assist.tracked);
        if (isRowShown(scoreFilter, highlighted)) {
            rows.push({
                key: index,
                time: formatGoalTime(goal),
                score: formatGoalScore(goal, t),
                scorer,
                detail: formatGoalDetail(goal, t),
                assists,
                videoUrl: goal.videoUrl,
            });
        }
    });
    return rows;
}

// Goalies who actually played, in the order the backend lists them.
function buildGoalieRows(game: Game, tracked: Set<PlayerId>, scoreFilter: ScoreFilter): GoalieRowModel[] {
    const rows: GoalieRowModel[] = [];
    game.goalies.forEach((goalie, index) => {
        if (goalie.timeOnIce === '00:00') {
            return;
        }
        const isTracked = tracked.has(goalie.playerId);
        if (isRowShown(scoreFilter, isTracked)) {
            rows.push({
                key: index,
                team: goalie.team,
                name: goalie.displayName,
                tracked: isTracked,
                saves: `${goalie.saves}/${goalie.shotsAgainst}`,
                savePct: `${(goalie.savePct * 100).toFixed(1)}%`,
            });
        }
    });
    return rows;
}

const daysBetween = (earlier: string | undefined, later: string): number =>
    earlier === undefined ? 0 : (new Date(later).getTime() - new Date(earlier).getTime()) / MS_PER_DAY;

const rosterEntry = (player: Player | undefined, gameDate: string): RosterEntry | undefined =>
    player === undefined
        ? undefined
        : {
            name: player.lastName,
            injury: player.injury,
            dimmed: player.injury > 0 || daysBetween(player.latestGame?.date, gameDate) > INACTIVE_AFTER_DAYS,
        };

// The tracked players of both teams side by side (home | away), alphabetically.
export function buildRoster(game: Game, players: readonly Player[]): RosterRowModel[] {
    const teamPlayers = (teamId: number) => players
        .filter(player => player.teamId === teamId && player.isActive)
        .sort((a, b) => compare(a.lastName, b.lastName));
    const home = teamPlayers(game.homeTeamId);
    const away = teamPlayers(game.awayTeamId);

    return Array.from({ length: Math.max(home.length, away.length) }, (_, index) => ({
        key: index,
        home: rosterEntry(home[index], game.date),
        away: rosterEntry(away[index], game.date),
    }));
}

// A game involves a hidden team when either side's abbreviation is in the list.
export function isHiddenByTeam(game: Game, hiddenTeams: readonly string[], teamAbbrev: TeamAbbrev = abbrevOfTeam): boolean {
    const sides = [teamAbbrev(game.homeTeamId), game.homeTeam, teamAbbrev(game.awayTeamId), game.awayTeam];
    return hiddenTeams.some(team => sides.includes(team));
}

// A game whose result is withheld: hidden by team, or started while scores are
// off, and not opened with its own "näytä" button.
export function isGameLocked(
    game: Game,
    scoreFilter: ScoreFilter,
    hiddenTeams: readonly string[],
    revealed: ReadonlySet<string>,
    teamAbbrev: TeamAbbrev = abbrevOfTeam,
): boolean {
    if (revealed.has(String(game.gameId))) {
        return false;
    }
    const started = !GameStatus.deriveStatus(game).isNotYetStarted;
    return isHiddenByTeam(game, hiddenTeams, teamAbbrev) || (scoreFilter === 'none' && started);
}

// The full model of one game card.
export function buildGameCard(game: Game, context: CardContext): GameCardModel {
    const { players, scoreFilter, hiddenTeams, revealed, t, language, now, teamAbbrev } = context;
    const status = GameStatus.deriveStatus(game);
    const started = !status.isNotYetStarted;
    const hiddenByTeam = isHiddenByTeam(game, hiddenTeams, teamAbbrev);

    let mode: CardMode = 'full';
    if (hiddenByTeam && !revealed) {
        mode = 'hidden-team';
    } else if (scoreFilter === 'none' && started && !revealed) {
        mode = 'locked';
    }

    const base = { key: game.gameId, status, home: game.homeTeam, away: game.awayTeam };
    const empty = { goals: [], goalies: [], hasBody: false };

    if (mode !== 'full') {
        // One line: who plays and, before the game, when. Nothing that gives a result away;
        // the video links stay available (the user opens them knowingly). A hidden game
        // that has not started also shows the playoff series standing and lists our players
        // (injuries included): all of it is from before the game, so it gives nothing away.
        const upcoming = mode === 'hidden-team' && status.isNotYetStarted;
        const roster = upcoming && (status.isScheduled || status.isPreGame || status.isTBD)
            ? buildRoster(game, players)
            : [];
        return {
            ...base,
            ...empty,
            mode,
            meta: upcoming ? formatMetaLine(game, status, scoreFilter, t) : undefined,
            roster: roster.length > 0 ? roster : undefined,
            hasBody: roster.length > 0,
            startLabel: status.isNotYetStarted ? formatStartLabel(game, status, t, now) : undefined,
            recapUrl: status.isFinalWithLinks ? game.recapUrl : undefined,
            longUrl: status.isFinalWithLinks ? game.extUrl : undefined,
        };
    }

    // Revealing a card lifts the "no scores" lock, nothing more.
    const filter: ScoreFilter = scoreFilter === 'none' ? 'all' : scoreFilter;
    const tracked = trackedIds(players);
    const isShown = status.isLive || status.isFinal;
    const goals = isShown ? buildGoalRows(game, tracked, filter, t, language) : [];
    const goalies = isShown ? buildGoalieRows(game, tracked, filter) : [];
    const showRoster = status.isScheduled || status.isPreGame || status.isTBD;
    const roster = showRoster ? buildRoster(game, players) : undefined;

    return {
        ...base,
        mode,
        score: isShown ? `${game.homeTeamGoals}–${game.awayTeamGoals}` : undefined,
        clock: status.isLive ? formatGameClock(game) : undefined,
        ending: status.isFinal ? formatEnding(status, t) : undefined,
        startLabel: status.isNotYetStarted ? formatStartLabel(game, status, t, now) : undefined,
        recapUrl: status.isFinalWithLinks ? game.recapUrl : undefined,
        longUrl: status.isFinalWithLinks ? game.extUrl : undefined,
        meta: formatMetaLine(game, status, filter, t),
        goals,
        goalies,
        roster: roster !== undefined && roster.length > 0 ? roster : undefined,
        hasBody: goals.length > 0 || goalies.length > 0 || (roster !== undefined && roster.length > 0),
    };
}
